import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, onSnapshot, where, deleteField } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { fetchLastSet, getExerciseMax } from '../firestoreUtils';
import { useSession } from '../context/SessionContext';
import { useProfile } from '../context/ProfileContext';
import { logWorkoutSession, reassignLogProfile, updateLogSet, deleteLogSet } from '../services/logsService';
import { updateExercise, mergeExercise, deleteExercise } from '../services/exercisesService';
import { groupLogs, FREESTYLE_KEY, dayKeyFor } from '../utils/groupLogs';
import { fetchExerciseNote } from '../services/exerciseNotesService';
import { MUSCLE_GROUPS, MUSCLE_TAXONOMY, isCardioExercise } from '../constants/exerciseTaxonomy';
import { formatLogAmount, formatCardioDurationShort, isCardioLog } from '../utils/formatLogAmount';
import AmountInput from './AmountInput';
import IconButton from './IconButton';
import CategoryPanel from './CategoryPanel';
import Modal from './Modal';
import ExercisePicker from './ExercisePicker';
import ExerciseSelectorModal from './ExerciseSelectorModal';
import ExerciseHistoryModal from './ExerciseHistoryModal';
import ExerciseNoteModal from './ExerciseNoteModal';
import ConfirmModal from './ConfirmModal';
import './comic.css';

// Everything below RoutinePicker in the authenticated dashboard. Lives INSIDE
// the ProfileProvider/ProfileGate subtree (unlike AppContent, which renders that
// provider), so it can read the active Hero via useProfile() directly. Owns the
// generic "Log a Workout" form + "Your Progress" list, and switches to the
// mission UI while a session is live — behaviorally identical to the previous
// inline AppContent render, just profile-scoped.
const WorkoutLog = ({ userId, activeTab }) => {
  const { sessionsByProfile } = useSession();
  const { activeProfile, profiles } = useProfile();
  // Quick Start/Manage My Exercises only block for the Hero currently
  // focused in ProfileSwitcher -- a different Hero's live mission elsewhere
  // on the account has no bearing on whether the FOCUSED Hero can use these.
  const focusedProfileHasSession = !!(activeProfile && sessionsByProfile[activeProfile.id]);

  // Misattribution recovery: which log entry's "Move to another Hero" picker
  // is currently open (id, or null). Only one open at a time — a fresh click
  // on a different entry's "Move" button replaces it rather than stacking.
  const [movingLogId, setMovingLogId] = useState(null);

  // Other non-archived Heroes on this account, excluding whichever profile's
  // history is currently being viewed — those are the only valid reassignment
  // targets. Only relevant with 2+ Heroes; single-Hero accounts have nothing
  // to move a log to.
  const otherProfiles = useMemo(
    () => profiles.filter((p) => p.isActive !== false && p.id !== activeProfile?.id),
    [profiles, activeProfile]
  );

  const handleReassignLog = async (logId, newProfileId) => {
    setMovingLogId(null);
    // The logs query below is scoped to profileId == activeProfile.id, so a
    // successful reassignment makes this entry disappear from view on its own
    // via the existing onSnapshot listener — no local state update needed.
    const result = await reassignLogProfile(logId, newProfileId);
    if (!result.success) {
      alert("Couldn't move that set. Try again.");
    }
  };

  const startEditLog = (log) => {
    setEditingLogId(log.id);
    if (isCardioLog(log)) {
      const total = Math.max(0, Math.round(Number(log.durationMinutes) || 0));
      setEditLogHours(total >= 60 ? String(Math.floor(total / 60)) : "");
      setEditLogMinutes(String(total % 60));
      setEditLogDistance(log.distance != null ? String(log.distance) : "");
    } else {
      setEditLogWeight(String(log.weight));
      setEditLogReps(String(log.reps));
    }
  };

  const cancelEditLog = () => {
    setEditingLogId(null);
    setEditLogWeight("");
    setEditLogReps("");
    setEditLogHours("");
    setEditLogMinutes("");
    setEditLogDistance("");
  };

  const handleSaveLogEdit = async (log) => {
    if (savingLogEdit || !activeProfile) return;
    const cardio = isCardioLog(log);

    if (cardio) {
      const totalMinutes = (Number(editLogHours) || 0) * 60 + (Number(editLogMinutes) || 0);
      if (totalMinutes <= 0) return;
      setSavingLogEdit(true);
      try {
        const distanceNum = editLogDistance ? Number(editLogDistance) : null;
        const result = await updateLogSet(log.id, {
          durationMinutes: totalMinutes,
          distance: distanceNum !== null ? distanceNum : deleteField()
        });
        if (!result.success) {
          alert("Couldn't update that session. Try again.");
          return;
        }
        cancelEditLog();
      } finally {
        setSavingLogEdit(false);
      }
      return;
    }

    // Weight is optional (bodyweight exercises) -- Number("") is 0, so a
    // blank weight correctly saves as 0 rather than blocking the edit.
    if (!editLogReps) return;

    setSavingLogEdit(true);
    try {
      const weightNum = Number(editLogWeight);
      const repsNum = Number(editLogReps);
      // Recompute isPR against the exercise's max excluding this log itself,
      // so correcting a weight down never leaves a stale PR badge (and
      // correcting one up can newly earn one).
      const maxExcludingThis = await getExerciseMax(userId, activeProfile.id, log.exerciseId, log.id);
      const isPR = weightNum > maxExcludingThis;

      const result = await updateLogSet(log.id, { weight: weightNum, reps: repsNum, isPR });
      if (!result.success) {
        alert("Couldn't update that set. Try again.");
        return;
      }
      // The logs onSnapshot listener reflects the edit automatically.
      cancelEditLog();
    } finally {
      setSavingLogEdit(false);
    }
  };

  const handleDeleteLog = async (logId) => {
    setDeletingLogId(logId);
    try {
      const result = await deleteLogSet(logId);
      if (!result.success) {
        alert("Couldn't delete that set. Try again.");
      }
      // The logs onSnapshot listener removes it from the list automatically.
    } finally {
      setDeletingLogId(null);
    }
  };

  const startAddSetToPanel = (panelKey) => {
    setAddingSetToPanel(panelKey);
    setNewSetExercise("");
    setNewSetWeight("");
    setNewSetReps("");
    setNewSetHours("");
    setNewSetMinutes("");
    setNewSetDistance("");
    setNewSetError(null);
  };

  const cancelAddSetToPanel = () => {
    setAddingSetToPanel(null);
    setNewSetExercise("");
    setNewSetWeight("");
    setNewSetReps("");
    setNewSetHours("");
    setNewSetMinutes("");
    setNewSetDistance("");
    setNewSetError(null);
  };

  // Adds a set to a PAST day/routine group. Backdates the write to noon
  // local time on that day (not serverTimestamp(), which would land it under
  // today) so it correctly buckets into the same group it was added from.
  const handleAddSetToPanel = async (group, routineGroup) => {
    if (!newSetExercise || savingNewSet) return;
    if (!activeProfile) return;

    const exerciseId = resolveExerciseId(newSetExercise);
    if (!exerciseId) {
      setNewSetError("Unknown exercise selected.");
      return;
    }
    if (group.key === "unknown") {
      setNewSetError("Can't add a set to an undated entry.");
      return;
    }

    const exerciseDoc = exerciseLibrary.find((ex) => ex.id === exerciseId);
    const cardio = isCardioExercise(exerciseDoc);
    const totalMinutes = cardio ? (Number(newSetHours) || 0) * 60 + (Number(newSetMinutes) || 0) : 0;
    if (cardio && totalMinutes <= 0) {
      setNewSetError("Log a duration.");
      return;
    }
    // Weight is optional (bodyweight exercises) -- Number("") is 0, so a
    // blank weight correctly logs as 0 rather than blocking the add.
    if (!cardio && !newSetReps) return;

    setSavingNewSet(true);
    setNewSetError(null);
    try {
      const [year, month, day] = group.key.split("-").map(Number);
      const backdate = new Date(year, month, day, 12, 0, 0);
      const isFreestyle = routineGroup.panelKey.endsWith(`::${FREESTYLE_KEY}`);

      const payload = {
        exerciseId,
        profileId: activeProfile.id,
        ownerUserId: userId,
        date: backdate,
        routineName: isFreestyle ? undefined : routineGroup.label,
        note: ""
      };

      if (cardio) {
        payload.durationMinutes = totalMinutes;
        if (newSetDistance) payload.distance = Number(newSetDistance);
      } else {
        const weightNum = Number(newSetWeight);
        const repsNum = Number(newSetReps);
        const currentMax = await getExerciseMax(userId, activeProfile.id, exerciseId);
        payload.weight = weightNum;
        payload.reps = repsNum;
        payload.isPR = weightNum > currentMax;
      }

      const result = await logWorkoutSession(payload);

      if (!result.success) {
        setNewSetError("Couldn't add that set. Try again.");
        return;
      }
      cancelAddSetToPanel();
    } finally {
      setSavingNewSet(false);
    }
  };

  const [exercise, setExercise] = useState("");
  const [weight, setWeight] = useState("");
  const [reps, setReps] = useState("");
  // Cardio counterparts to weight/reps above -- only one pair is ever
  // actually shown/used at a time, picked by whether the selected exercise
  // is Cardio (see quickStartIsCardio below).
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [distance, setDistance] = useState("");
  // True once the current weight/reps (or hours/minutes/distance) have
  // already been written via logCurrentSet and haven't been touched since —
  // lets "Save Workout" tell "user just logged this and is now finishing
  // up" (skip re-logging) apart from "user typed something new and hasn't
  // logged it yet" (log it first). Reset to false on any field edit or
  // exercise change.
  const [pendingLogged, setPendingLogged] = useState(false);
  const [logs, setLogs] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [loading, setLoading] = useState(false);

  // Open/closed state for the per-routine history panels. Keyed by
  // `${dateKey}::${routineKey}` so the same routine on two different days
  // collapses independently. Everything defaults to collapsed (not present in
  // the Set) — per the request, all the user initially sees is the routine name.
  const [openPanels, setOpenPanels] = useState(() => new Set());

  const togglePanel = (panelKey) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      if (next.has(panelKey)) next.delete(panelKey);
      else next.add(panelKey);
      return next;
    });
  };

  // Inline "Add Exercise" affordance state. New exercises are owner-scoped
  // (private to this account, shared across its Heroes); the dropdown updates
  // itself via the custom-exercise onSnapshot listener below, so no manual
  // refresh is needed on success.
  const [addingExercise, setAddingExercise] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState("");
  const [newExerciseGroup, setNewExerciseGroup] = useState("");
  const [newExerciseMain, setNewExerciseMain] = useState("");
  const [savingExercise, setSavingExercise] = useState(false);
  const [exerciseError, setExerciseError] = useState(null);
  // Non-null while editing an existing custom exercise (holds its id); null
  // in "add new" mode. Shares the form/state above with add-exercise.
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  // Merge-a-custom-exercise state, shares the "Manage My Exercises" modal
  // with the add/edit form above (only one of list/add/merge shows at a
  // time). mergingExerciseId holds the source exercise being folded away;
  // mergeTarget is the exercise it's being merged into, picked via
  // ExercisePicker; mergeConfirming gates the explicit "are you sure" step
  // since this is a destructive, unrecoverable action.
  const [mergingExerciseId, setMergingExerciseId] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeConfirming, setMergeConfirming] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [mergeResultMessage, setMergeResultMessage] = useState(null);

  // Quick Start's exercise field, and Manage My Exercises' "+ Add Exercise"
  // trigger, both open the same ExerciseSelectorModal popup (search / browse
  // by body part / create new) rather than an inline combobox or form.
  const [showQuickStartPicker, setShowQuickStartPicker] = useState(false);
  const [showAddExercisePicker, setShowAddExercisePicker] = useState(false);

  // Which exercise's "i" history popup is open: { exerciseId, name } or null.
  const [historyExercise, setHistoryExercise] = useState(null);
  // Which entry's note popup is open -- from "Previously..." (a past day)
  // or Quick Start (always "today," same as ActiveMission's own note icon):
  // { exerciseId, name, dayKey, dayLabel, tabKey, source } or null. `source`
  // is only set to "quickstart" by Quick Start's trigger, so its onSaved
  // handler below can tell "this save should update the Quick Start note
  // icon's color" apart from an unrelated edit from "Previously..." that
  // happens to also land on today's date.
  const [noteEntry, setNoteEntry] = useState(null);
  // Whether the currently-selected Quick Start exercise already has a note
  // for today -- drives the note icon's color (green/"Edit" vs. dark/"Add"),
  // same convention as ActiveMission's own per-exercise todayNotes map, just
  // a single value since Quick Start only ever has one exercise selected at
  // a time. Refetched whenever the selected exercise changes.
  const [quickStartHasNote, setQuickStartHasNote] = useState(false);
  // Which "Previously..." log entry (full log object) is pending "Delete
  // this set?" confirmation, or null.
  const [confirmDeleteLog, setConfirmDeleteLog] = useState(null);
  const [deletingLogConfirm, setDeletingLogConfirm] = useState(false);
  // Which Quick Start recap set (full set object) is pending "Delete this
  // set?" confirmation, or null.
  const [confirmDeleteQuickStartSet, setConfirmDeleteQuickStartSet] = useState(null);
  const [deletingQuickStartSetConfirm, setDeletingQuickStartSetConfirm] = useState(false);

  // Delete a custom exercise outright (distinct from merge -- no history
  // repointing, existing logs/routines referencing it fall back to the
  // app's existing "(deleted exercise)" display, same as an out-of-band
  // Firebase-console deletion always has).
  const [deletingExerciseId, setDeletingExerciseId] = useState(null);
  const [deletingExercise, setDeletingExercise] = useState(false);
  const [deleteExerciseError, setDeleteExerciseError] = useState(null);

  useEffect(() => {
    if (!mergeResultMessage) return undefined;
    const timer = setTimeout(() => setMergeResultMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [mergeResultMessage]);

  // Past-workout editing: which log's inline edit form is open (id, or null),
  // plus its draft weight/reps (strength) or hours/minutes/distance (cardio
  // -- startEditLog picks which set of fields to prefill via isCardioLog on
  // the log itself, not the exercise doc, since that's authoritative for
  // what was actually recorded). Only one open at a time.
  const [editingLogId, setEditingLogId] = useState(null);
  const [editLogWeight, setEditLogWeight] = useState("");
  const [editLogReps, setEditLogReps] = useState("");
  const [editLogHours, setEditLogHours] = useState("");
  const [editLogMinutes, setEditLogMinutes] = useState("");
  const [editLogDistance, setEditLogDistance] = useState("");
  const [savingLogEdit, setSavingLogEdit] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState(null);

  // Adding a set to a PAST day/routine group: which panel's add-set form is
  // open (routineGroup.panelKey, or null), plus its draft fields -- either
  // weight/reps or hours/minutes/distance, depending on whether the picked
  // exercise is Cardio. The new log is backdated to that day (noon local
  // time) so it correctly buckets into the same group instead of landing
  // under today.
  const [addingSetToPanel, setAddingSetToPanel] = useState(null);
  const [newSetExercise, setNewSetExercise] = useState("");
  const [newSetWeight, setNewSetWeight] = useState("");
  const [newSetReps, setNewSetReps] = useState("");
  const [newSetHours, setNewSetHours] = useState("");
  const [newSetMinutes, setNewSetMinutes] = useState("");
  const [newSetDistance, setNewSetDistance] = useState("");
  const [newSetError, setNewSetError] = useState(null);
  const [savingNewSet, setSavingNewSet] = useState(false);

  // Quick Start's in-progress local session: sets logged via "Log Set" so
  // far, shown live in-panel so the user can see what they've done without
  // opening "Previously..." (mirrors ActiveMission's loggedSets pattern).
  // Purely local/ephemeral display state — each set is already written to
  // Firestore immediately when logged via logCurrentSet, same as before;
  // this just recaps them in-place. Cleared when "Complete" finalizes.
  const [quickStartSets, setQuickStartSets] = useState([]);
  // Non-blocking inline feedback (validation/write errors), same auto-dismiss
  // pattern as ActiveMission's `bubble` — a blocking alert() on every set
  // would be a bad fit now that Quick Start logs multiple sets in a row.
  const [quickStartBubble, setQuickStartBubble] = useState(null);
  // "Cancel" for Quick Start -- the same abandon-everything-logged-this-
  // session action ActiveMission's own Cancel Mission has, per direct user
  // request to give Quick Start an equivalent Complete/Cancel row.
  const [quickStartCancelConfirming, setQuickStartCancelConfirming] = useState(false);
  const [cancellingQuickStart, setCancellingQuickStart] = useState(false);

  useEffect(() => {
    if (!quickStartBubble) return undefined;
    const timer = setTimeout(() => setQuickStartBubble(null), 3200);
    return () => clearTimeout(timer);
  }, [quickStartBubble]);

  // Styling constants
  const mobileInputStyle = { display: "block", width: "100%", height: "48px", margin: "10px 0px", padding: "10px", fontSize: "16px", borderRadius: "8px", border: "1px solid #ccc" };

  // Fetch Logs — now scoped to the active Hero, not just the account.
  useEffect(() => {
    if (!userId || !activeProfile) return undefined;

    const q = query(
      collection(db, "logs"),
      where("ownerUserId", "==", userId),
      where("profileId", "==", activeProfile.id),
      orderBy("date", "desc")
    );
    return onSnapshot(q, (snapshot) => {
      const entries = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(log => log.exerciseId !== "SESSION_START");
      setLogs(entries);
    });
  }, [userId, activeProfile]);

  // Fetch Exercise Library — merged two-tier fetch. The library is split
  // between the official/curated docs (legacy userId == "global") and this
  // account's own custom exercises (ownerUserId == the Manager's uid). Rules
  // scope reads per-branch, so we run TWO listeners (one per query shape) and
  // merge into a single map, deduped by doc id, into exerciseLibrary state.
  useEffect(() => {
    if (!userId) return undefined;

    const merged = new Map();
    const publish = () => setExerciseLibrary(Array.from(merged.values()));

    const applySnapshot = (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "removed") merged.delete(change.doc.id);
        else merged.set(change.doc.id, { id: change.doc.id, ...change.doc.data() });
      });
      publish();
    };

    const officialUnsub = onSnapshot(
      query(collection(db, "exercises"), where("userId", "==", "global")),
      applySnapshot
    );
    const customUnsub = onSnapshot(
      query(collection(db, "exercises"), where("ownerUserId", "==", userId)),
      applySnapshot
    );

    return () => { officialUnsub(); customUnsub(); };
  }, [userId]);

  // Resolve the Firestore exercise doc id from the selected name.
  // Logs carry exerciseId (not the name) under the unified schema.
  const resolveExerciseId = (name) => {
    const match = exerciseLibrary.find((ex) => ex.name === name);
    return match ? match.id : null;
  };

  // Resolve a display name from an exerciseId for the progress list.
  const resolveExerciseName = (exerciseId) => {
    const match = exerciseLibrary.find((ex) => ex.id === exerciseId);
    return match ? match.name : exerciseId;
  };

  // Group logs into calendar-day buckets for the "Previously..." history list.
  // logs arrive newest-first from the Firestore query (orderBy date desc), so a
  // straight reduce that appends to the current group preserves that ordering
  // both across groups (newest day first) and within each group (newest set
  // first). Grouping key is the local-time calendar day (year-month-day) via
  // Date, and each group carries a pre-formatted human-readable header
  // (e.g. "Saturday, July 11"). Purely a render-time transform — no query,
  // state, or write-path changes.
  const groupedLogs = useMemo(() => groupLogs(logs), [logs]);

  // Logic for "Repeat Last Set" -- prefills weight/reps, or (for a Cardio
  // exercise) hours/minutes/distance instead, from whichever this account
  // last logged for the newly-selected exercise. Always clears BOTH field
  // sets first (not just the one about to be filled), so switching from a
  // strength exercise to a Cardio one, or back, never leaves a stale value
  // from the other shape sitting in a now-hidden field that'd otherwise get
  // silently submitted if the user switches back again.
  const handleExerciseChange = async (selectedExerciseName) => {
    setExercise(selectedExerciseName);
    setPendingLogged(false);
    setWeight("");
    setReps("");
    setHours("");
    setMinutes("");
    setDistance("");
    setQuickStartHasNote(false);
    if (!userId || !selectedExerciseName || !activeProfile) return;

    const exerciseId = resolveExerciseId(selectedExerciseName);
    if (!exerciseId) return;

    // Independent of the "repeat last set" fetch below (and its early
    // return when there's no prior set) -- a note can exist for today even
    // when nothing's been logged yet.
    fetchExerciseNote(userId, activeProfile.id, exerciseId, dayKeyFor(new Date())).then((note) => {
      setQuickStartHasNote(!!note);
    });

    const cardio = isCardioExercise(exerciseLibrary.find((ex) => ex.id === exerciseId));
    const lastSet = await fetchLastSet(userId, activeProfile.id, exerciseId);
    if (!lastSet) return;

    if (cardio) {
      const total = Math.max(0, Math.round(Number(lastSet.durationMinutes) || 0));
      setHours(total >= 60 ? String(Math.floor(total / 60)) : "");
      setMinutes(String(total % 60));
      setDistance(lastSet.distance != null ? String(lastSet.distance) : "");
    } else {
      setWeight(lastSet.weight.toString());
      setReps(lastSet.reps.toString());
    }
  };

  // Logs whatever is currently in the exercise/weight/reps fields as one set
  // (no routineName — same "Freestyle" convention as before) and appends it
  // to the live quickStartSets recap. Weight/reps are left as-is afterward
  // (not cleared) so the next set defaults to repeating the same numbers —
  // most sets of one exercise use the same weight, and it's a one-tap fix if
  // not. Exercise selection is also left as-is, so several sets of the same
  // exercise in a row never need re-picking it. Shared by both "Log Set"
  // and "Complete" (the latter logs one final pending set, if any, before
  // finalizing). Returns true/false so callers know whether the
  // set-in-progress was actually recorded.
  const logCurrentSet = async () => {
    if (!exercise) {
      setQuickStartBubble("Select an exercise.");
      return false;
    }

    const exerciseId = resolveExerciseId(exercise);
    if (!exerciseId) {
      setQuickStartBubble("Unknown exercise selected.");
      return false;
    }

    // Defensive: never fall back to writing an unscoped "N/A" log.
    if (!activeProfile) {
      setQuickStartBubble("No active Hero selected. Set not logged.");
      return false;
    }

    const cardio = isCardioExercise(exerciseLibrary.find((ex) => ex.id === exerciseId));

    if (cardio) {
      const totalMinutes = (Number(hours) || 0) * 60 + (Number(minutes) || 0);
      if (totalMinutes <= 0) {
        setQuickStartBubble("Log a duration, Hero.");
        return false;
      }

      setLoading(true);
      try {
        const distanceNum = distance ? Number(distance) : undefined;
        const payload = {
          exerciseId,
          profileId: activeProfile.id,
          ownerUserId: userId,
          durationMinutes: totalMinutes,
          note: ""
        };
        if (distanceNum !== undefined) payload.distance = distanceNum;

        const result = await logWorkoutSession(payload);
        if (!result.success) {
          setQuickStartBubble("Error saving workout. Session not logged.");
          return false;
        }

        setQuickStartSets((prev) => [
          ...prev,
          { id: result.id, exerciseId, exerciseName: exercise, durationMinutes: totalMinutes, distance: distanceNum }
        ]);
        setPendingLogged(true);
        return true;
      } catch (e) {
        setQuickStartBubble("Error saving workout. Session not logged.");
        return false;
      } finally {
        setLoading(false);
      }
    }

    // Weight is optional (bodyweight exercises) -- Number("") is 0, so a
    // blank weight correctly logs as a 0-weight set rather than blocking.
    if (!reps) {
      setQuickStartBubble("Select an exercise and enter reps.");
      return false;
    }

    setLoading(true);
    try {
      const currentMax = await getExerciseMax(userId, activeProfile.id, exerciseId);
      const weightNum = Number(weight);
      const repsNum = Number(reps);
      const isPR = weightNum > currentMax;

      // Route through the shared log service so all write paths share one schema.
      const result = await logWorkoutSession({
        exerciseId,
        profileId: activeProfile.id,
        ownerUserId: userId,
        weight: weightNum,
        reps: repsNum,
        isPR,
        note: ""
      });

      if (!result.success) {
        setQuickStartBubble("Error saving workout. Set not logged.");
        return false;
      }

      setQuickStartSets((prev) => [
        ...prev,
        { id: result.id, exerciseId, exerciseName: exercise, weight: weightNum, reps: repsNum, isPR }
      ]);
      setPendingLogged(true);
      return true;
    } catch (e) {
      setQuickStartBubble("Error saving workout. Set not logged.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleLogSet = () => {
    logCurrentSet();
  };

  // "Complete" finalizes the local Quick Start session: if there's a
  // pending, not-yet-logged set in the form, log it first, then close out
  // the recap. pendingLogged distinguishes "these numbers are already
  // written and untouched since" (just finalize) from "user typed something
  // new" (log it first) — without it, carried-forward values from the last
  // "Log Set" would get logged a second time on every "Complete".
  const handleFinishWorkout = async () => {
    if ((weight || reps || hours || minutes || distance) && !pendingLogged) {
      const success = await logCurrentSet();
      if (!success) return;
    }
    setExercise("");
    setWeight("");
    setReps("");
    setHours("");
    setMinutes("");
    setDistance("");
    setPendingLogged(false);
    setQuickStartSets([]);
  };

  // "Cancel Mission": abandon the whole Quick Start session, deleting every
  // set logged so far (quickStartSets already holds each one's Firestore doc
  // id, no extra query needed) and discarding whatever's currently typed in
  // the form but not yet logged -- same semantics as ActiveMission's own
  // Cancel Mission ("this didn't happen," not "here's what was done").
  const handleCancelQuickStart = async () => {
    setCancellingQuickStart(true);
    await Promise.all(quickStartSets.map((s) => deleteLogSet(s.id)));
    setCancellingQuickStart(false);
    setExercise("");
    setWeight("");
    setReps("");
    setHours("");
    setMinutes("");
    setDistance("");
    setPendingLogged(false);
    setQuickStartSets([]);
    setQuickStartCancelConfirming(false);
  };

  const handleDeleteQuickStartSet = async (setId) => {
    const result = await deleteLogSet(setId);
    if (!result.success) {
      setQuickStartBubble("Couldn't delete that set. Try again.");
      return;
    }
    setQuickStartSets((prev) => prev.filter((s) => s.id !== setId));
  };

  const confirmAndDeleteQuickStartSet = async () => {
    if (!confirmDeleteQuickStartSet || deletingQuickStartSetConfirm) return;
    setDeletingQuickStartSetConfirm(true);
    await handleDeleteQuickStartSet(confirmDeleteQuickStartSet.id);
    setDeletingQuickStartSetConfirm(false);
    setConfirmDeleteQuickStartSet(null);
  };

  const confirmAndDeleteLog = async () => {
    if (!confirmDeleteLog || deletingLogConfirm) return;
    setDeletingLogConfirm(true);
    await handleDeleteLog(confirmDeleteLog.id);
    setDeletingLogConfirm(false);
    setConfirmDeleteLog(null);
  };

  const resetMergeState = () => {
    setMergingExerciseId(null);
    setMergeTarget(null);
    setMergeConfirming(false);
    setMergeError(null);
  };

  const resetExerciseForm = () => {
    setNewExerciseName("");
    setNewExerciseGroup("");
    setNewExerciseMain("");
    setExerciseError(null);
    setAddingExercise(false);
    setEditingExerciseId(null);
    resetMergeState();
  };

  // Resets any in-progress add/edit-exercise form on navigating away from
  // the "Manage My Exercises" tab, matching the old collapsible panel's
  // "closing resets the form" behavior — so coming back to this tab later
  // always starts at the plain list, not an abandoned form. (The merge
  // Modal resets itself the same way automatically, via its own `tabKey`.)
  useEffect(() => {
    if (activeTab !== "exercises") resetExerciseForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Open the add-exercise form pre-filled with an existing custom exercise,
  // in edit mode. Only ever called for this account's own custom docs — the
  // "Your Exercises" list below never surfaces official/global ones.
  const startEditExercise = (ex) => {
    resetMergeState();
    setExerciseError(null);
    setNewExerciseName(ex.name || "");
    setNewExerciseGroup(ex.targetMuscle || "");
    setNewExerciseMain(ex.mainMuscle || "");
    setEditingExerciseId(ex.id);
    setAddingExercise(true);
  };

  // Open the merge sub-view for an existing custom exercise (the "Manage My
  // Exercises" modal shows list / add-edit-form / merge as three mutually
  // exclusive views, same convention as add vs. edit sharing one form).
  const startMergeExercise = (ex) => {
    resetExerciseForm();
    setMergingExerciseId(ex.id);
  };

  // Folds mergingExerciseId's logged sets/routine references into
  // mergeTarget, then deletes the now-redundant source exercise. Requires
  // the explicit mergeConfirming step to have already been shown/accepted —
  // this is destructive and can't be undone, unlike a name/muscle edit.
  const handleConfirmMerge = async () => {
    if (!mergingExerciseId || !mergeTarget || merging) return;
    setMerging(true);
    setMergeError(null);
    const result = await mergeExercise(mergingExerciseId, mergeTarget.id, userId);
    setMerging(false);
    if (result.success) {
      const setsText = `${result.logsUpdated} logged set${result.logsUpdated === 1 ? "" : "s"}`;
      const routinesText = result.routinesUpdated
        ? ` and ${result.routinesUpdated} routine${result.routinesUpdated === 1 ? "" : "s"}`
        : "";
      setMergeResultMessage(`Merged into "${mergeTarget.name}". Moved ${setsText}${routinesText}.`);
      resetMergeState();
    } else {
      setMergeError("Couldn't merge that exercise. Try again.");
      setMergeConfirming(false);
    }
  };

  // Edit-only now -- creating a new custom exercise happens inside
  // ExerciseSelectorModal instead (its own self-contained create flow),
  // reached via Quick Start's exercise field or "+ Add Exercise" here. This
  // form only ever renders in edit mode (addingExercise is only ever set
  // true together with editingExerciseId, via startEditExercise below).
  const handleUpdateExercise = async (e) => {
    e.preventDefault();
    const name = newExerciseName.trim();
    const group = newExerciseGroup;
    const main = newExerciseMain;
    if (!name || !group || savingExercise) return;

    // Client-side duplicate guard against the already-loaded merged library
    // (official + this account's custom docs). Match is case-insensitive and
    // trimmed on both sides, full-name (not substring). Excludes the exercise
    // currently being edited, so saving it under its own unchanged name
    // doesn't false-positive as a duplicate of itself.
    const isDuplicate = exerciseLibrary.some(
      (ex) => ex.id !== editingExerciseId && ex.name?.trim().toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      setExerciseError(`An exercise named "${name}" already exists.`);
      return;
    }

    setSavingExercise(true);
    setExerciseError(null);
    const result = await updateExercise(editingExerciseId, name, group, main);
    if (result.success) {
      // The onSnapshot listener reflects the update in exerciseLibrary
      // automatically, so the list entry updates on its own.
      resetExerciseForm();
    } else {
      setExerciseError("Couldn't update that exercise. Try again.");
    }
    setSavingExercise(false);
  };

  // Delete outright (as opposed to merge, which repoints history first).
  // deleteExercise's own ownership guard (must be this account's custom doc)
  // is belt-and-suspenders on top of this button only ever being offered on
  // this account's own rows below.
  const handleConfirmDeleteExercise = async () => {
    if (!deletingExerciseId || deletingExercise) return;
    setDeletingExercise(true);
    setDeleteExerciseError(null);
    const result = await deleteExercise(deletingExerciseId, userId);
    setDeletingExercise(false);
    if (result.success) {
      setDeletingExerciseId(null);
    } else {
      setDeleteExerciseError("Couldn't delete that exercise. Try again.");
    }
  };

  const mergingExercise = mergingExerciseId
    ? exerciseLibrary.find((ex) => ex.id === mergingExerciseId)
    : null;

  // Complete / Cancel row -- shared markup so the two reproductions (top and
  // bottom of Quick Start) can't drift out of sync, mirroring ActiveMission's
  // own 2-button missionActionsRow exactly. "Log Set" (formerly "Next," part
  // of this same row) moved out per direct user request -- it's no longer a
  // shared top/bottom action; it's now a single full-width button sitting
  // directly under the last input field (see quickStartLogSetButton below),
  // right where a Hero's thumb already is after typing a set's numbers,
  // rather than requiring a reach up/down to a row shared with two
  // unrelated whole-session actions.
  const quickStartButtonStyle = {
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  };
  // Complete/Cancel stay disabled until at least one set has actually been
  // logged this session -- nothing to finalize or discard before then.
  const quickStartNoSetsLogged = quickStartSets.length === 0;
  const quickStartActionsRow = (
    <div style={{ display: "flex", gap: "8px" }}>
      <button
        onClick={handleFinishWorkout}
        disabled={loading || quickStartNoSetsLogged}
        className="comic-btn comic-btn--done"
        style={quickStartButtonStyle}
      >
        {loading ? "Saving..." : "Complete"}
      </button>
      <button
        type="button"
        onClick={() => setQuickStartCancelConfirming(true)}
        disabled={loading || quickStartNoSetsLogged}
        className="comic-btn comic-btn--end"
        style={quickStartButtonStyle}
      >
        Cancel
      </button>
    </div>
  );

  // Full-width "Log Set" button, rendered once directly under the last
  // input field (Reps for a strength exercise, Distance for Cardio) -- see
  // quickStartActionsRow's comment above for why this moved out of the
  // shared button row.
  const quickStartLogSetButton = (
    <button
      onClick={handleLogSet}
      disabled={loading}
      className="comic-btn comic-btn--dark"
    >
      {loading ? "Logging..." : "Log Set"}
    </button>
  );

  const quickStartIsCardio = isCardioExercise(exerciseLibrary.find((ex) => ex.name === exercise));
  const newSetIsCardio = isCardioExercise(exerciseLibrary.find((ex) => ex.name === newSetExercise));

  return (
    <>
      <div style={{ display: activeTab === "quickstart" ? "block" : "none" }}>
        <CategoryPanel
          mascotVariant="quickstart"
          outerClassName="comic-panel--category comic-panel--border-peach comic-panel--no-top-margin"
          title="Quick Start"
        >
          {focusedProfileHasSession ? (
            <p className="comic-empty">Finish or cancel {activeProfile?.name}'s current Mission (on the Select Mission tab) to use Quick Start.</p>
          ) : (
          <>
          {quickStartActionsRow}

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setShowQuickStartPicker(true)}
              className="comic-input"
              style={{ ...mobileInputStyle, flex: 1, margin: "10px 0", textAlign: "left", cursor: "pointer" }}
            >
              {exercise || "Select or Add Exercise"}
            </button>
            {exercise && (
              <>
                <IconButton
                  icon="note"
                  onClick={() => setNoteEntry({
                    exerciseId: resolveExerciseId(exercise),
                    name: exercise,
                    dayKey: dayKeyFor(new Date()),
                    dayLabel: "Today",
                    tabKey: "quickstart",
                    source: "quickstart"
                  })}
                  color={quickStartHasNote ? "#00a274" : "#232526"}
                  label={`${quickStartHasNote ? "Edit" : "Add"} note for ${exercise}`}
                />
                <IconButton
                  icon="info"
                  onClick={() => setHistoryExercise({ exerciseId: resolveExerciseId(exercise), name: exercise, tabKey: "quickstart" })}
                  color="#232526"
                  label={`View history for ${exercise}`}
                />
              </>
            )}
          </div>
          {showQuickStartPicker && (
            <ExerciseSelectorModal
              exercises={exerciseLibrary}
              userId={userId}
              tabKey="quickstart"
              title="Select Exercise"
              onSelect={(ex) => handleExerciseChange(ex.name)}
              onClose={() => setShowQuickStartPicker(false)}
            />
          )}

          {quickStartIsCardio ? (
            <>
              <AmountInput
                value={hours}
                onChange={(e) => { setHours(e.target.value); setPendingLogged(false); }}
                unit="hr"
                placeholder="Hours"
                inputClassName=""
                inputStyle={mobileInputStyle}
                wrapperStyle={{ marginBottom: "10px" }}
              />
              <AmountInput
                value={minutes}
                onChange={(e) => { setMinutes(e.target.value); setPendingLogged(false); }}
                unit="min"
                placeholder="Minutes"
                inputClassName=""
                inputStyle={mobileInputStyle}
                wrapperStyle={{ marginBottom: "10px" }}
              />
              <AmountInput
                value={distance}
                onChange={(e) => { setDistance(e.target.value); setPendingLogged(false); }}
                unit=""
                placeholder="Dist. (optional)"
                inputClassName=""
                inputStyle={mobileInputStyle}
                wrapperStyle={{ marginBottom: "10px" }}
              />
            </>
          ) : (
            <>
              <AmountInput
                value={weight}
                onChange={(e) => { setWeight(e.target.value); setPendingLogged(false); }}
                unit="lb"
                placeholder="Weight"
                inputClassName=""
                inputStyle={mobileInputStyle}
                wrapperStyle={{ marginBottom: "10px" }}
              />

              <AmountInput
                value={reps}
                onChange={(e) => { setReps(e.target.value); setPendingLogged(false); }}
                unit="reps"
                placeholder="Reps"
                inputClassName=""
                inputStyle={mobileInputStyle}
                wrapperStyle={{ marginBottom: "10px" }}
              />
            </>
          )}

          {quickStartLogSetButton}

          {quickStartActionsRow}

          {quickStartBubble && <div className="comic-alert-bubble">{quickStartBubble}</div>}

          {quickStartSets.length > 0 && (
            <div className="comic-panel" style={{ marginTop: "12px", padding: "10px" }}>
              {quickStartSets.map((set) => (
                <div key={set.id} className="stamp-row">
                  <span className="stamp-row__badge stamp-row__badge--check">&#10003;</span>
                  <span className="stamp-row__text">
                    {set.exerciseName}: {formatLogAmount(set)}
                  </span>
                  {set.isPR && <span className="pr-spark">🔥</span>}
                  <IconButton
                    icon="delete"
                    onClick={() => setConfirmDeleteQuickStartSet(set)}
                    color="#232526"
                    label={`Delete set: ${set.exerciseName}`}
                  />
                </div>
              ))}
            </div>
          )}
          </>
          )}
        </CategoryPanel>
      </div>

      <div style={{ display: activeTab === "exercises" ? "block" : "none" }}>
        <CategoryPanel
          mascotVariant="exercises"
          outerClassName="comic-panel--category comic-panel--border-peach comic-panel--no-top-margin"
          title="Manage My Exercises"
        >
          {focusedProfileHasSession ? (
            <p className="comic-empty">Finish or cancel {activeProfile?.name}'s current Mission (on the Select Mission tab) to manage your exercises.</p>
          ) : (
            <>
              {exerciseLibrary
                .filter((ex) => ex.ownerUserId === userId)
                .map((ex) => (
                  <div key={ex.id} className="stamp-row">
                    <span className="stamp-row__text">{ex.name} ({ex.targetMuscle} · {ex.mainMuscle || "—"})</span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <IconButton
                        icon="info"
                        onClick={() => setHistoryExercise({ exerciseId: ex.id, name: ex.name, tabKey: "exercises" })}
                        color="#232526"
                        label={`View history for ${ex.name}`}
                      />
                      <IconButton
                        icon="edit"
                        onClick={() => startEditExercise(ex)}
                        color="#232526"
                        label={`Edit ${ex.name}`}
                      />
                      <IconButton
                        icon="swap"
                        onClick={() => startMergeExercise(ex)}
                        color="#232526"
                        label={`Merge ${ex.name} into another exercise`}
                      />
                      <IconButton
                        icon="delete"
                        onClick={() => { setDeleteExerciseError(null); setDeletingExerciseId(ex.id); }}
                        color="#232526"
                        label={`Delete ${ex.name}`}
                      />
                    </div>
                  </div>
                ))}

              {mergeResultMessage && <div className="comic-alert-bubble">{mergeResultMessage}</div>}

              {deletingExerciseId && (() => {
                const target = exerciseLibrary.find((ex) => ex.id === deletingExerciseId);
                return (
                  <Modal title="Delete Exercise?" tabKey="exercises" onClose={() => setDeletingExerciseId(null)}>
                    <p style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "14px", color: "#232526", margin: "0 0 14px 0" }}>
                      Delete "{target?.name}"? This can't be undone. Any logged sets or routines using it will show as "(deleted exercise)" afterward — if you'd rather keep that history intact, use Merge instead.
                    </p>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <button
                        type="button"
                        onClick={handleConfirmDeleteExercise}
                        disabled={deletingExercise}
                        className="comic-btn comic-btn--end"
                        style={{ flex: 1 }}
                      >
                        {deletingExercise ? "Deleting..." : "Delete"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingExerciseId(null)}
                        disabled={deletingExercise}
                        className="comic-btn comic-btn--done"
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </button>
                    </div>
                    {deleteExerciseError && <div className="comic-alert-bubble">{deleteExerciseError}</div>}
                  </Modal>
                );
              })()}

              {mergingExerciseId && (
                <Modal title={`Merge "${mergingExercise?.name}"`} tabKey="exercises" onClose={resetMergeState}>
                  {!mergeConfirming && (
                    <>
                      <p style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "13px", color: "#232526", marginBottom: "10px" }}>
                        Pick the exercise to fold it into. Every logged set and routine using "{mergingExercise?.name}" moves over, and it gets deleted.
                      </p>
                      <ExercisePicker
                        exercises={exerciseLibrary.filter((ex) => ex.id !== mergingExerciseId)}
                        value={mergeTarget?.name || ""}
                        onSelect={(ex) => setMergeTarget(ex)}
                      />
                      <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                        <button
                          type="button"
                          onClick={() => setMergeConfirming(true)}
                          disabled={!mergeTarget}
                          className="comic-btn comic-btn--go"
                          style={{
                            flex: 1,
                            fontSize: "13px",
                            padding: "0 6px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {mergeTarget ? `Merge into "${mergeTarget.name}"` : "Merge"}
                        </button>
                        <button
                          type="button"
                          onClick={resetMergeState}
                          className="comic-btn comic-btn--end"
                          style={{ flex: 1, height: "46px", marginTop: "10px", fontSize: "17px" }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                  {mergeConfirming && (
                    <div className="comic-alert-bubble">
                      This can't be undone — merge "{mergingExercise?.name}" into "{mergeTarget?.name}"?
                      <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                        <button
                          type="button"
                          onClick={handleConfirmMerge}
                          disabled={merging}
                          className="comic-btn comic-btn--go"
                          style={{ flex: 1 }}
                        >
                          {merging ? "Merging..." : "Yes, Merge"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setMergeConfirming(false)}
                          disabled={merging}
                          className="comic-btn comic-btn--end"
                          style={{ flex: 1, height: "46px", marginTop: "10px", fontSize: "17px" }}
                        >
                          Back
                        </button>
                      </div>
                    </div>
                  )}
                  {mergeError && <div className="comic-alert-bubble">{mergeError}</div>}
                </Modal>
              )}

              {addingExercise && editingExerciseId ? (
                <form onSubmit={handleUpdateExercise} style={{ marginTop: "10px" }}>
                  <h4 className="comic-narration-date" style={{ marginTop: 0 }}>
                    Edit Exercise
                  </h4>
                  <input
                    type="text"
                    placeholder="Exercise name"
                    value={newExerciseName}
                    onChange={(e) => setNewExerciseName(e.target.value)}
                    className="comic-input"
                    style={{ width: "100%", marginBottom: "10px" }}
                    autoFocus
                  />
                  <select
                    value={newExerciseGroup}
                    onChange={(e) => { setNewExerciseGroup(e.target.value); setNewExerciseMain(""); }}
                    className="comic-input"
                    style={{ width: "100%", marginBottom: "10px" }}
                  >
                    <option value="">Select Muscle Group</option>
                    {MUSCLE_GROUPS.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  <select
                    value={newExerciseMain}
                    onChange={(e) => setNewExerciseMain(e.target.value)}
                    disabled={!newExerciseGroup}
                    className="comic-input"
                    style={{ width: "100%" }}
                  >
                    <option value="">Select Main Muscle (Optional)</option>
                    {(MUSCLE_TAXONOMY[newExerciseGroup] || []).map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    disabled={savingExercise || !newExerciseName.trim() || !newExerciseGroup}
                    className="comic-btn comic-btn--go"
                  >
                    {savingExercise ? "Saving..." : "Update Exercise"}
                  </button>
                  <button type="button" onClick={resetExerciseForm} className="comic-btn comic-btn--end">
                    Cancel
                  </button>
                  {exerciseError && <div className="comic-alert-bubble">{exerciseError}</div>}
                </form>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAddExercisePicker(true)}
                  className="comic-btn comic-btn--go"
                  style={{ marginTop: "10px" }}
                >
                  + Add Exercise
                </button>
              )}

              {showAddExercisePicker && (
                <ExerciseSelectorModal
                  exercises={exerciseLibrary}
                  userId={userId}
                  tabKey="exercises"
                  title="Add Exercise"
                  startCreating
                  onSelect={() => {}}
                  onClose={() => setShowAddExercisePicker(false)}
                />
              )}
            </>
          )}
        </CategoryPanel>
      </div>

      <div style={{ display: activeTab === "history" ? "block" : "none" }}>
      <CategoryPanel
        mascotVariant="history"
        outerClassName="comic-panel--category comic-panel--border-peach comic-panel--no-top-margin"
        title="Previously..."
      >
      <>
      {(logs.length === 0 ? <p>No logs yet. Start lifting!</p> : (
        groupedLogs.map((group) => (
          <div key={group.key}>
            <h4 className="comic-narration-date">{group.header}</h4>
            {group.routineGroups.map((routineGroup) => {
              const isOpen = openPanels.has(routineGroup.panelKey);
              return (
                <div key={routineGroup.panelKey}>
                  <button
                    type="button"
                    onClick={() => togglePanel(routineGroup.panelKey)}
                    aria-expanded={isOpen}
                    className="comic-panel comic-panel--entry comic-tap"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      width: "100%",
                      color: "#232526",
                      fontFamily: 'Anonymous Pro',
                      fontWeight: "bold",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "16px"
                    }}
                  >
                    <span>{routineGroup.label}</span>
                    <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span>
                  </button>
                  {isOpen && (
                    <ul>
                      {routineGroup.entries.map((log) => (
                        <li key={log.id} style={{ marginBottom: "10px" }}>
                          {editingLogId === log.id ? (
                            <div>
                              <div>{resolveExerciseName(log.exerciseId)}:</div>
                              <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", marginTop: "6px" }}>
                                {isCardioLog(log) ? (
                                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <AmountInput
                                      value={editLogHours}
                                      onChange={(e) => setEditLogHours(e.target.value)}
                                      unit="hr"
                                      wrapperStyle={{ width: "70px" }}
                                      inputStyle={{ height: "30px", padding: "4px 26px 4px 6px", fontSize: "13px" }}
                                    />
                                    <AmountInput
                                      value={editLogMinutes}
                                      onChange={(e) => setEditLogMinutes(e.target.value)}
                                      unit="min"
                                      wrapperStyle={{ width: "70px" }}
                                      inputStyle={{ height: "30px", padding: "4px 30px 4px 6px", fontSize: "13px" }}
                                    />
                                    <AmountInput
                                      value={editLogDistance}
                                      onChange={(e) => setEditLogDistance(e.target.value)}
                                      unit="mi"
                                      wrapperStyle={{ width: "70px" }}
                                      inputStyle={{ height: "30px", padding: "4px 26px 4px 6px", fontSize: "13px" }}
                                    />
                                  </span>
                                ) : (
                                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <AmountInput
                                      value={editLogWeight}
                                      onChange={(e) => setEditLogWeight(e.target.value)}
                                      unit="lb"
                                      wrapperStyle={{ width: "80px" }}
                                      inputStyle={{ height: "30px", padding: "4px 30px 4px 6px", fontSize: "13px" }}
                                    />
                                    x
                                    <AmountInput
                                      value={editLogReps}
                                      onChange={(e) => setEditLogReps(e.target.value)}
                                      unit="reps"
                                      wrapperStyle={{ width: "80px" }}
                                      inputStyle={{ height: "30px", padding: "4px 30px 4px 6px", fontSize: "13px" }}
                                    />
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleSaveLogEdit(log)}
                                  disabled={savingLogEdit || (!isCardioLog(log) && !editLogReps)}
                                  style={{
                                    background: "none", border: "none", color: "#00a274",
                                    fontFamily: 'Anonymous Pro', fontWeight: "bold", fontSize: "13px",
                                    cursor: "pointer", textDecoration: "underline"
                                  }}
                                >
                                  {savingLogEdit ? "Saving..." : "Save"}
                                </button>
                                <IconButton
                                  icon="close"
                                  onClick={cancelEditLog}
                                  color="#232526"
                                  label="Cancel editing this set"
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div>
                                {/* Cardio entries use the same compact
                                    "H:MM, Dist=D" wording ActiveMission's
                                    "Previous" column uses, per direct user
                                    request to match the two -- strength
                                    entries are unchanged ("132 lb x 8
                                    reps"), same as every other cardio
                                    display in the app besides that one
                                    column. */}
                                {resolveExerciseName(log.exerciseId)}: {isCardioLog(log) ? formatCardioDurationShort(log.durationMinutes, log.distance) : formatLogAmount(log)} {log.isPR ? "🔥" : ""}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", marginTop: "6px" }}>
                                <IconButton
                                  icon="edit"
                                  onClick={() => startEditLog(log)}
                                  color="#232526"
                                  label={`Edit ${resolveExerciseName(log.exerciseId)} set`}
                                />
                                <IconButton
                                  icon="note"
                                  onClick={() => setNoteEntry({
                                    exerciseId: log.exerciseId,
                                    name: resolveExerciseName(log.exerciseId),
                                    dayKey: group.key,
                                    dayLabel: group.header
                                  })}
                                  color="#232526"
                                  label={`Note for ${resolveExerciseName(log.exerciseId)} on ${group.header}`}
                                />
                                <IconButton
                                  icon="delete"
                                  onClick={() => setConfirmDeleteLog(log)}
                                  disabled={deletingLogId === log.id}
                                  color="#232526"
                                  label={`Delete ${resolveExerciseName(log.exerciseId)} set`}
                                />
                                {otherProfiles.length > 0 && (
                                  movingLogId === log.id ? (
                                    <span style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                      Move to:
                                      {otherProfiles.map((p) => (
                                        <button
                                          key={p.id}
                                          type="button"
                                          onClick={() => handleReassignLog(log.id, p.id)}
                                          style={{
                                            background: "none",
                                            border: "none",
                                            color: "#00a274",
                                            fontFamily: 'Anonymous Pro',
                                            fontWeight: "bold",
                                            fontSize: "13px",
                                            cursor: "pointer",
                                            textDecoration: "underline"
                                          }}
                                        >
                                          {p.name}
                                        </button>
                                      ))}
                                      <IconButton
                                        icon="close"
                                        onClick={() => setMovingLogId(null)}
                                        color="#232526"
                                        label="Cancel moving this set"
                                      />
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setMovingLogId(log.id)}
                                      style={{
                                        background: "none",
                                        border: "none",
                                        color: "#fd874e",
                                        fontFamily: 'Anonymous Pro',
                                        fontSize: "13px",
                                        cursor: "pointer",
                                        textDecoration: "underline"
                                      }}
                                    >
                                      Wrong Hero?
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {isOpen && (
                    addingSetToPanel === routineGroup.panelKey ? (
                      <div style={{ padding: "8px 4px" }}>
                        <ExercisePicker
                          exercises={exerciseLibrary}
                          value={newSetExercise}
                          onSelect={(ex) => setNewSetExercise(ex.name)}
                          inputStyle={mobileInputStyle}
                        />
                        {newSetIsCardio ? (
                          <>
                            <AmountInput
                              value={newSetHours}
                              onChange={(e) => setNewSetHours(e.target.value)}
                              unit="hr"
                              placeholder="Hours"
                              inputClassName=""
                              inputStyle={mobileInputStyle}
                              wrapperStyle={{ marginBottom: "10px" }}
                            />
                            <AmountInput
                              value={newSetMinutes}
                              onChange={(e) => setNewSetMinutes(e.target.value)}
                              unit="min"
                              placeholder="Minutes"
                              inputClassName=""
                              inputStyle={mobileInputStyle}
                              wrapperStyle={{ marginBottom: "10px" }}
                            />
                            <AmountInput
                              value={newSetDistance}
                              onChange={(e) => setNewSetDistance(e.target.value)}
                              unit="mi"
                              placeholder="Dist. (optional)"
                              inputClassName=""
                              inputStyle={mobileInputStyle}
                              wrapperStyle={{ marginBottom: "10px" }}
                            />
                          </>
                        ) : (
                          <>
                            <AmountInput
                              value={newSetWeight}
                              onChange={(e) => setNewSetWeight(e.target.value)}
                              unit="lb"
                              placeholder="Weight"
                              inputClassName=""
                              inputStyle={mobileInputStyle}
                              wrapperStyle={{ marginBottom: "10px" }}
                            />
                            <AmountInput
                              value={newSetReps}
                              onChange={(e) => setNewSetReps(e.target.value)}
                              unit="reps"
                              placeholder="Reps"
                              inputClassName=""
                              inputStyle={mobileInputStyle}
                              wrapperStyle={{ marginBottom: "10px" }}
                            />
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => handleAddSetToPanel(group, routineGroup)}
                          disabled={savingNewSet || !newSetExercise || (!newSetIsCardio && !newSetReps)}
                          className="comic-btn comic-btn--go"
                        >
                          {savingNewSet ? "Saving..." : "Add Set"}
                        </button>
                        <button type="button" onClick={cancelAddSetToPanel} className="comic-btn comic-btn--end">
                          Cancel
                        </button>
                        {newSetError && <div className="comic-alert-bubble">{newSetError}</div>}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startAddSetToPanel(routineGroup.panelKey)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#fd874e",
                          fontFamily: 'Anonymous Pro',
                          fontSize: "13px",
                          cursor: "pointer",
                          textDecoration: "underline",
                          margin: "4px 0 8px 4px"
                        }}
                      >
                        + Add Set
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        ))
      ))}
      </>
      </CategoryPanel>
      </div>

      {historyExercise && (
        <ExerciseHistoryModal
          exerciseId={historyExercise.exerciseId}
          exerciseName={historyExercise.name}
          userId={userId}
          tabKey={historyExercise.tabKey}
          onClose={() => setHistoryExercise(null)}
        />
      )}

      {noteEntry && (
        <ExerciseNoteModal
          exerciseId={noteEntry.exerciseId}
          exerciseName={noteEntry.name}
          dayKey={noteEntry.dayKey}
          dayLabel={noteEntry.dayLabel}
          userId={userId}
          tabKey={noteEntry.tabKey || "history"}
          onClose={() => setNoteEntry(null)}
          onSaved={(text) => {
            if (noteEntry.source === "quickstart") setQuickStartHasNote(!!text);
          }}
        />
      )}

      {confirmDeleteQuickStartSet && (
        <ConfirmModal
          title="Delete Set?"
          message={`Delete this logged set of ${confirmDeleteQuickStartSet.exerciseName} (${formatLogAmount(confirmDeleteQuickStartSet)})? This can't be undone.`}
          confirming={deletingQuickStartSetConfirm}
          onConfirm={confirmAndDeleteQuickStartSet}
          onCancel={() => setConfirmDeleteQuickStartSet(null)}
          tabKey="quickstart"
        />
      )}

      {quickStartCancelConfirming && (
        <ConfirmModal
          title="Cancel Mission?"
          message={`Discard this mission? ${quickStartSets.length} set${quickStartSets.length === 1 ? "" : "s"} logged so far will be deleted.`}
          confirmLabel="Discard"
          confirmingLabel="Discarding..."
          cancelLabel="Keep Going"
          confirming={cancellingQuickStart}
          onConfirm={handleCancelQuickStart}
          onCancel={() => setQuickStartCancelConfirming(false)}
          tabKey="quickstart"
        />
      )}

      {confirmDeleteLog && (
        <ConfirmModal
          title="Delete Set?"
          message={`Delete this logged set of ${resolveExerciseName(confirmDeleteLog.exerciseId)} (${formatLogAmount(confirmDeleteLog)})? This can't be undone.`}
          confirming={deletingLogConfirm}
          onConfirm={confirmAndDeleteLog}
          onCancel={() => setConfirmDeleteLog(null)}
          tabKey="history"
        />
      )}
    </>
  );
};

export default WorkoutLog;
